import { Button, IconButton } from '@ads/components-react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/shared/ui/molecules';

import type { SerieClassStudentFormValue } from '../../../types/serieForm';
import { AddTurmaModal } from '../AddTurmaModal';

export interface ClassStudentsSectionProps {
  readonly classStudents: readonly SerieClassStudentFormValue[];
  readonly onAdd: (classStudent: SerieClassStudentFormValue) => void;
  readonly onRemove: (classStudentId: number) => void;
}

/**
 * Seção "Turmas" do formulário de Série — só renderizada quando `Geral` está
 * desmarcado (ver `SerieForm`). Lista local (sem paginação, altura máxima com
 * scroll) das turmas vinculadas; adicionar/remover só altera o array local —
 * a persistência ocorre no "Salvar" do formulário inteiro da Série (não existe
 * endpoint de vínculo incremental turma↔série).
 */
export function ClassStudentsSection({
  classStudents,
  onAdd,
  onRemove,
}: ClassStudentsSectionProps) {
  const { t } = useTranslation('series');
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="flex flex-col gap-mini">
      <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border pb-nano">
        <h3 className="ts-heading-xxs text-ctx-content-title">
          {t('series.classStudents.heading')}
        </h3>
        <Button
          hierarchy="primary"
          size="md"
          leadingIcon={PlusIcon}
          type="button"
          onClick={() => setModalOpen(true)}
        >
          {t('series.classStudents.addButton')}
        </Button>
      </div>

      <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxxs">
        <div className="flex items-center gap-mini border-b-hairline border-ctx-layout-border px-xxs py-mini">
          <span className="flex-1 ts-paragraph-xxs !font-semibold text-ctx-content-title">
            {t('series.classStudents.table.columns.class')}
          </span>
          <span className="w-[70px] text-right ts-paragraph-xxs !font-semibold text-ctx-content-title">
            {t('series.classStudents.table.columns.actions')}
          </span>
        </div>

        {classStudents.length === 0 ? (
          <EmptyState
            title={t('series.classStudents.table.empty')}
            description={t('series.classStudents.table.emptyDescription')}
          />
        ) : (
          <div className="max-h-[204px] overflow-y-auto">
            {classStudents.map(cs => (
              <div
                key={cs.classStudentId}
                className="flex items-center gap-mini border-b-hairline border-ctx-layout-border px-nano py-nano last:border-b-0"
              >
                <span className="flex-1 ts-paragraph-xxs text-ctx-content-primary">
                  {cs.description}
                </span>
                <div className="flex w-[70px] justify-end">
                  <IconButton
                    icon={TrashIcon}
                    ariaLabel={t('series.classStudents.table.columns.actions')}
                    hierarchy="tertiary"
                    className="!text-base-danger-600"
                    size="md"
                    type="button"
                    onClick={() => onRemove(cs.classStudentId)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AddTurmaModal open={modalOpen} onOpenChange={setModalOpen} onAdd={onAdd} />
    </div>
  );
}
